import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import { Form, FormSection } from 'components/common/form';
import { UserSummaryEmailConfiguration } from 'components/common/form';

import type { User } from 'shared-types';

const ProfileSummaryEmail = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { user },
    serverConfig
  } = useReduxState();

  const [isSaving, setIsSaving] = useState(false);

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const calendarEventsVisible = !serverConfig.hideCalendarEventsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setIsSaving(true);

      await api.post(`/auth/profile/`, {
        ...values
      });

      dispatch.updatePageState({
        user: { ...user, ...values }
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      await dispatch.handleErrorDispatch(error);
    }
  };

  const summaryOptions: {
    label: string;
    value: keyof User['profileSummaryEmailOptions'];
  }[] = [
    ...(coursesVisible
      ? [
          {
            label: messages.profileSummaryEmailCoursesAddedTo,
            value: 'coursesAddedTo' as const
          }
        ]
      : []),
    ...(pathsVisible
      ? [
          {
            label: messages.profileSummaryEmailLearningPathsAddedTo,
            value: 'learningPathsAddedTo' as const
          }
        ]
      : []),
    ...(achievementsVisible
      ? [
          {
            label: messages.profileSummaryEmailAchievementsAddedTo,
            value: 'achievementsAddedTo' as const
          }
        ]
      : []),
    ...(calendarEventsVisible
      ? [
          {
            label: messages.profileSummaryEmailCalendarEventsAddedTo,
            value: 'calendarEventsAddedTo' as const
          }
        ]
      : []),
    ...(coursesVisible || pathsVisible
      ? [
          {
            label: messages.commonUpcomingTargetSchedules,
            value: 'upcomingDeadlines' as const
          }
        ]
      : []),
    ...(coursesVisible || achievementsVisible
      ? [
          {
            label: messages.completionsExpiring,
            value: 'expiringCompletions' as const
          }
        ]
      : []),
    ...(coursesVisible || achievementsVisible
      ? [
          {
            label: messages.completionsExpired,
            value: 'expiredCompletions' as const
          }
        ]
      : []),
    ...(coursesVisible
      ? [
          {
            label: messages.tasksOpenQuestionAnswerReviews,
            value: 'openQuestionAnswerReviews' as const
          }
        ]
      : [])
  ];

  const initialValues = {
    profileSummaryEmailEnabled: serverConfig.profileSummaryEmailEnabled
      ? true
      : user?.profileSummaryEmailEnabled,
    profileSummaryEmailFrequency: serverConfig.profileSummaryEmailEnabled
      ? serverConfig.profileSummaryEmailFrequency
      : user?.profileSummaryEmailFrequency,
    profileSummaryEmailOptions: serverConfig.profileSummaryEmailEnabled
      ? {
          ...user?.profileSummaryEmailOptions,
          ...Object.keys(serverConfig.profileSummaryEmailOptions || {}).reduce(
            (options, option) => {
              if (serverConfig.profileSummaryEmailOptions[option]) {
                options[option] = true;
              }
              return options;
            },
            {}
          )
        }
      : user?.profileSummaryEmailOptions
  };

  return (
    <Box padding={0} title={messages.commonSettings}>
      <Form initialValues={initialValues} onSubmit={handleSubmit}>
        {({ formState }) => (
          <>
            <UserSummaryEmailConfiguration
              isSiteSettingsView={false}
              summaryOptions={summaryOptions}
            />
            <FormSection>
              <Button
                category="success"
                disabled={formState.pristine}
                loading={isSaving}
                text={messages.commonSave}
                type="submit"
              />
            </FormSection>
          </>
        )}
      </Form>
    </Box>
  );
};

export default ProfileSummaryEmail;
